import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { authApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface AuthPageProps {
  mode: 'login' | 'register';
}

export default function Auth({ mode }: AuthPageProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get redirect path from location state or default to /app
  const from = (location.state as { from?: string })?.from || '/app';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Client-side validation for registration
    if (mode === 'register') {
      if (password.length < 6) {
        setError(lang === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
        return;
      }
      if (!name.trim()) {
        setError(lang === 'ar' ? 'الاسم مطلوب' : 'Name is required');
        return;
      }
    }
    
    setLoading(true);

    try {
      if (mode === 'login') {
        const { user, token } = await authApi.login(email, password);
        setUser(user);
        setToken(token);
      } else {
        // Extract just 'en' or 'ar' from language codes like 'en-US'
        const langCode = lang.startsWith('ar') ? 'ar' : 'en';
        const { user, token } = await authApi.signup(email, password, name, langCode);
        setUser(user);
        setToken(token);
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      // Try to get error message from API response
      let errorMessage: string;
      if (err?.message) {
        errorMessage = err.message;
      } else if (mode === 'login') {
        errorMessage = lang === 'ar' ? 'بيانات الدخول غير صحيحة' : 'Invalid email or password';
      } else {
        errorMessage = lang === 'ar' ? 'فشل إنشاء الحساب. تأكد من صحة البيانات.' : 'Failed to create account. Please check your details.';
      }
      setError(errorMessage);
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft className="h-4 w-4 flip-rtl" />
            {lang === 'ar' ? 'العودة للرئيسية' : 'Back to home'}
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-lg">
              M
            </div>
            <span className="text-2xl font-bold">MRSpace</span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">
              {mode === 'login'
                ? lang === 'ar' ? 'مرحباً بعودتك' : 'Welcome back'
                : lang === 'ar' ? 'أنشئ حسابك' : 'Create your account'}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'login'
                ? lang === 'ar' ? 'سجل دخولك للمتابعة' : 'Sign in to continue to your workspace'
                : lang === 'ar' ? 'ابدأ رحلتك مع MRSpace' : 'Start your journey with MRSpace'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {lang === 'ar' ? 'الاسم' : 'Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input w-full"
                  placeholder={lang === 'ar' ? 'أدخل اسمك' : 'Enter your name'}
                  required
                  dir="auto"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input w-full"
                placeholder="you@example.com"
                required
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {lang === 'ar' ? 'كلمة المرور' : 'Password'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input w-full pe-10"
                  placeholder={lang === 'ar' ? 'أدخل كلمة المرور' : 'Enter your password'}
                  required
                  minLength={6}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-11 text-base"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : mode === 'login' ? (
                lang === 'ar' ? 'تسجيل الدخول' : 'Sign in'
              ) : (
                lang === 'ar' ? 'إنشاء الحساب' : 'Create account'
              )}
            </button>
          </form>

          {/* Switch mode */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === 'login' ? (
              <>
                {lang === 'ar' ? 'ليس لديك حساب؟ ' : "Don't have an account? "}
                <Link to="/register" className="text-primary font-medium hover:underline">
                  {lang === 'ar' ? 'سجل الآن' : 'Sign up'}
                </Link>
              </>
            ) : (
              <>
                {lang === 'ar' ? 'لديك حساب بالفعل؟ ' : 'Already have an account? '}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {lang === 'ar' ? 'سجل دخولك' : 'Sign in'}
                </Link>
              </>
            )}
          </p>

          {/* Language switcher */}
          <div className="mt-8 flex justify-center">
            <LanguageSwitcher variant="inline" />
          </div>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/20 via-purple-500/10 to-background items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="h-24 w-24 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-4xl font-bold shadow-xl">
            M
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {lang === 'ar' ? 'مساحة عملك الذكية' : 'Your Smart Workspace'}
          </h2>
          <p className="text-muted-foreground">
            {lang === 'ar'
              ? 'دوّن أفكارك، نظّم مشاريعك، وتعاون مع فريقك بسهولة'
              : 'Write, plan, and collaborate seamlessly with your team'}
          </p>
        </div>
      </div>
    </div>
  );
}
